import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Users, UserCheck, Route as RouteIcon, Inbox, ClipboardList, Timer, FileCheck, CheckCircle2,
  AlertTriangle, ShieldAlert, StickyNote, BadgeCheck, Loader2, Plus, Building2, School, Baby,
  ClipboardCheck, UserPlus, ArrowRight, CalendarDays, Siren, Lock, ScrollText,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import RoleBadge from "@/components/RoleBadge";
import StaffAvatar from "@/components/StaffAvatar";
import { CaseStatusBadge } from "@/pages/Children";

const STAT_DEFS = [
  { key: "total_active_children", label: "Total Active Children", icon: Users, link: "/children" },
  { key: "active_travel_trainers", label: "Active Travel Trainers", icon: UserCheck, link: "/staff" },
  { key: "active_training_programmes", label: "Active Training Programmes", icon: RouteIcon, link: "/programmes" },
  { key: "new_referrals", label: "New Referrals", icon: Inbox, link: "/children" },
  { key: "referrals_awaiting_review", label: "Referrals Awaiting Review", icon: ClipboardCheck, link: "/children" },
  { key: "initial_assessments_scheduled", label: "Initial Assessments Scheduled", icon: ClipboardList, link: "/children" },
  { key: "assessments_awaiting", label: "Assessments In Progress", icon: ClipboardList, link: "/children" },
  { key: "assessments_awaiting_review", label: "Assessments Awaiting Manager Review", icon: FileCheck, link: "/children" },
  { key: "assessments_further_info", label: "Further Information Required", icon: AlertTriangle, link: "/children" },
  { key: "suitable_for_training", label: "Suitable for Training", icon: CheckCircle2, link: "/children" },
  { key: "not_currently_suitable", label: "Not Currently Suitable", icon: ShieldAlert, link: "/children" },
  { key: "plans_awaiting_review", label: "Route Plans Awaiting Review", icon: RouteIcon, link: "/children" },
  { key: "ras_awaiting_review", label: "Risk Assessments Awaiting Review", icon: AlertTriangle, link: "/children" },
  { key: "ras_due_review", label: "Risk Assessments Due for Review", icon: Timer, link: "/children" },
  { key: "route_changes_open", label: "Route Changes Requiring Action", icon: AlertTriangle, link: "/children" },
  { key: "approved_plans", label: "Approved Journeys Awaiting Training Start", icon: CheckCircle2, link: "/children" },
  { key: "children_awaiting_initial_assessment", label: "Children Awaiting Initial Assessment", icon: ClipboardList, link: "/children" },
  { key: "children_awaiting_trainer", label: "Children Awaiting Trainer Assignment", icon: UserPlus, link: "/children" },
  { key: "programmes_nearing_completion", label: "Programmes Nearing Completion", icon: Timer, link: "/programmes" },
  { key: "programmes_paused", label: "Programmes Paused", icon: Timer, link: "/programmes" },
  { key: "programmes_extended", label: "Programmes Extended", icon: Timer, link: "/programmes" },
  { key: "programmes_behind_schedule", label: "Programmes Behind Schedule", icon: AlertTriangle, link: "/programmes" },
  { key: "sessions_with_interventions", label: "Sessions With Interventions", icon: AlertTriangle, link: "/programmes" },
  { key: "outstanding_session_notes", label: "Outstanding Session Notes", icon: StickyNote, link: "/submitted-reports" },
  { key: "risk_reviews_open", label: "Risk Reviews Requested", icon: ShieldAlert, link: "/children" },
  { key: "incidents_new", label: "Incidents Awaiting Review", icon: Siren, link: "/incidents" },
  { key: "incidents_critical", label: "Critical Incidents", icon: Siren, link: "/incidents" },
  { key: "near_misses", label: "Near Misses", icon: AlertTriangle, link: "/incidents" },
  { key: "incident_actions_open", label: "Open Incident Actions", icon: ClipboardCheck, link: "/incidents" },
  { key: "incident_actions_overdue", label: "Overdue Actions", icon: AlertTriangle, link: "/incidents" },
  { key: "safeguarding_new", label: "New Safeguarding Concerns", icon: Lock, link: "/safeguarding" },
  { key: "safeguarding_urgent", label: "Urgent Safeguarding Reviews", icon: Lock, link: "/safeguarding" },
  { key: "final_assessments_due", label: "Final Assessments Due", icon: ClipboardCheck, link: "/programmes" },
  { key: "final_assessments_in_progress", label: "Final Assessments In Progress", icon: ClipboardCheck, link: "/programmes" },
  { key: "awaiting_manager_approval", label: "Awaiting Manager Approval", icon: ClipboardCheck, link: "/programmes" },
  { key: "completed_this_month", label: "Completed This Month", icon: Timer, link: "/reports" },
  { key: "completed_this_year", label: "Completed This Year", icon: Timer, link: "/reports" },
  { key: "certificates_issued", label: "Certificates Issued", icon: ScrollText, link: "/reports" },
  { key: "completed_programmes", label: "Completed Programmes", icon: CheckCircle2, link: "/reports" },
  { key: "incidents_requiring_review", label: "Incidents Requiring Review", icon: AlertTriangle, link: "/incidents" },
  { key: "safeguarding_attention", label: "Safeguarding Actions Requiring Attention", icon: ShieldAlert, link: "/safeguarding" },
  { key: "outstanding_staff_notes", label: "Outstanding Staff Notes", icon: StickyNote, link: "/submitted-reports" },
  { key: "staff_compliance_alerts", label: "Staff Compliance Alerts", icon: BadgeCheck, link: "/staff" },
  { key: "feedback_outstanding", label: "Feedback Forms Outstanding", icon: ClipboardList, link: "/submitted-reports" },
  { key: "feedback_escalated", label: "Feedback Escalated to Co-ordinator", icon: AlertTriangle, link: "/submitted-reports" },
  { key: "objectives_overdue", label: "Objectives Past Target Date", icon: AlertTriangle, link: "/programmes" },
  { key: "spot_checks_overdue", label: "Spot Checks Overdue", icon: ClipboardCheck, link: "/spot-checks" },
  { key: "verification_reviews_overdue", label: "Verification Reviews Outstanding", icon: ClipboardCheck, link: "/children" },
];

const OUTCOME_STYLES = {
  success: "bg-ukgreen-light text-ukgreen border-transparent",
  denied: "bg-amber-100 text-amber-800 border-transparent",
  failure: "bg-red-50 text-red-700 border-transparent",
};

const fmtTime = (v) => {
  try {
    return format(new Date(v), "dd MMM HH:mm");
  } catch {
    return "";
  }
};

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();
  const [statItems, setStatItems] = useState(null);
  const [statTitle, setStatTitle] = useState("");

  const openStat = async (e, key, label, link, value) => {
    e.preventDefault();
    if (!value) { navigate(link || "/dashboard"); return; }
    try {
      const { data } = await api.get(`/dashboard/stat-items/${key}`);
      const items = data.items || [];
      if (items.length === 1) navigate(items[0].link);
      else if (items.length > 1) { setStatTitle(label); setStatItems(items); }
      else navigate(link || "/dashboard");
    } catch {
      navigate(link || "/dashboard");
    }
  };
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => setError("Could not load dashboard summary."));
  }, []);

  const isAdmin = hasPermission("staff:read");
  const canSeeAttendance = hasPermission("attendance:view_all");
  const [exceptions, setExceptions] = useState(null);
  useEffect(() => {
    if (!canSeeAttendance) return undefined;
    const load = () => api.get("/attendance/exceptions").then((r) => setExceptions(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [canSeeAttendance]);

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
          <div className="mt-2 flex items-center gap-3">
            <RoleBadge role={user?.role} testid="dashboard-role-badge" />
            <span className="text-sm text-slate-600">{user?.staff_number}</span>
          </div>
        </div>
      </div>

      {error && (
        <div data-testid="dashboard-error" role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error}
        </div>
      )}

      {!summary && !error && (
        <div className="flex items-center gap-2 text-slate-600" data-testid="dashboard-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading summary…
        </div>
      )}

      {summary && (
        <>
          {STAT_DEFS.some((d) => summary.stats?.[d.key] !== undefined && summary.stats?.[d.key] !== null) && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-testid="dashboard-stats">
            {STAT_DEFS.map(({ key, label, icon: Icon, link }) => {
              const value = summary.stats?.[key];
              if (value === undefined || value === null) return null;  // restricted stats are never rendered
              const alert = key === "staff_compliance_alerts" && typeof value === "number" && value > 0;
              return (
                <Link key={key} to={link || "/dashboard"} data-testid={`stat-card-${key}`}
                  onClick={(e) => openStat(e, key, label, link, value)}
                  className="group block rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  title={`Open ${label}`}>
                  <Card className={`relative overflow-hidden h-full hover:shadow-md hover:border-primary/40 transition-all duration-200 ${alert ? "border-amber-300" : ""}`}>
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-cyan-500 via-green-500 to-amber-400" aria-hidden="true" />
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-slate-600 leading-tight">{label}</p>
                        <Icon className={`h-4 w-4 shrink-0 ${alert ? "text-amber-600" : "text-primary"}`} />
                      </div>
                      <p className={`font-heading text-2xl font-bold mt-1 ${alert ? "text-amber-700" : "text-slate-900"}`} data-testid={`stat-value-${key}`}>
                        {value === null || value === undefined ? "—" : value}
                      </p>
                      <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        Open <ArrowRight className="h-3 w-3" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          )}

          <Dialog open={!!statItems} onOpenChange={(v) => !v && setStatItems(null)}>
            <DialogContent className="bg-white sm:max-w-md max-h-[70vh] overflow-y-auto" data-testid="stat-items-dialog">
              <DialogHeader>
                <DialogTitle>{statTitle}</DialogTitle>
                <DialogDescription>Select a case to open it directly.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                {(statItems || []).map((item) => (
                  <button key={item.id} type="button" data-testid={`stat-item-${item.id}`}
                    onClick={() => { setStatItems(null); navigate(item.link); }}
                    className="w-full rounded-md border border-border bg-white p-3 text-left hover:border-primary/50 hover:bg-slate-50 transition-colors duration-150">
                    <p className="text-sm font-medium text-slate-900">{item.title}</p>
                    {item.subtitle && <p className="text-xs text-slate-500">{item.subtitle}</p>}
                  </button>
                ))}
              </div>
            </DialogContent>
          </Dialog>

          {user?.role === "travel_trainer" && summary.my_training && (
            <Card data-testid="trainer-quick-actions">
              <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Link to="/incidents/new" data-testid="trainer-report-incident"
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-red-600 text-sm font-semibold text-white hover:bg-red-700 transition-colors duration-200">
                  <Siren className="h-4 w-4" /> Report incident
                </Link>
                <Link to="/safeguarding/new" data-testid="trainer-raise-safeguarding"
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-md border border-red-300 text-sm font-semibold text-red-700 hover:bg-red-50 transition-colors duration-200">
                  <Lock className="h-4 w-4" /> Raise safeguarding concern
                </Link>
              </CardContent>
            </Card>
          )}

          {user?.role === "travel_trainer" && summary.my_incident_actions?.length > 0 && (
            <Card data-testid="trainer-incident-actions">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ClipboardCheck className="h-5 w-5 text-amber-600" /> My outstanding incident follow-ups
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {summary.my_incident_actions.map((a, i) => (
                  <Link key={i} to={`/incidents/${a.incident_id}`} data-testid={`my-incident-action-${i}`}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors duration-200 ${a.overdue ? "border-red-200 bg-red-50 text-red-900" : "border-border hover:bg-muted"}`}>
                    <span className="font-mono text-xs">{a.incident_number}</span>
                    <span className="flex-1">{a.action}</span>
                    <span className="text-xs">{a.overdue ? "OVERDUE" : `due ${a.due_date || "—"}`}</span>
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}

          {user?.role === "travel_trainer" && summary.my_final_assessments?.length > 0 && (
            <Card data-testid="trainer-final-assessments">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ClipboardCheck className="h-5 w-5 text-primary" /> My final assessments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {summary.my_final_assessments.map((f) => (
                  <Link key={f.id} to={`/final-assessments/${f.id}`} data-testid={`my-final-${f.final_number}`}
                    className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted transition-colors duration-200">
                    <span className="font-mono text-xs">{f.final_number}</span>
                    <span className="text-slate-600">{f.case_id}</span>
                    <span className="text-slate-500">{f.direction === "outbound" ? "Home → School" : "School → Home"}</span>
                    <span className="ml-auto text-xs text-slate-500">{(f.status || "").replace(/_/g, " ")}</span>
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}

          {user?.role === "travel_trainer" && summary.my_training && (
            <Card data-testid="trainer-training-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarDays className="h-5 w-5 text-primary" /> My training
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const overdueTotal = (summary.my_training.programmes || []).reduce((n, p) => n + (p.objectives_overdue || 0), 0);
                  if (!overdueTotal) return null;
                  return (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="objectives-overdue-badge">
                      <span className="font-semibold">{overdueTotal} objective{overdueTotal > 1 ? "s" : ""} past target date</span>
                      {" "}across your programmes — review them on each programme's Weekly Reviews tab.
                    </div>
                  );
                })()}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Today's training</p>
                  {summary.my_training.today?.length ? (
                    <div className="space-y-2">
                      {summary.my_training.today.map((s) => (
                        <Link key={s.id} to={`/sessions/${s.id}`} data-testid={`today-session-${s.id}`}
                          className="flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm hover:bg-primary/10 transition-colors duration-200">
                          <span className="font-medium text-slate-900">{s.child_name}</span>
                          <span className="text-slate-600">{s.planned_start_time || "—"}</span>
                          <span className="text-slate-600">{s.direction === "outbound" ? "Home → School" : s.direction === "return" ? "School → Home" : "Other"}</span>
                          {s.training_level && (
                            <Badge className="bg-blue-50 text-blue-700 border-transparent" title={s.training_level_name || undefined}
                              data-testid={`today-level-${s.id}`}>L{s.training_level}{s.training_level_name ? ` ${s.training_level_name}` : ""}</Badge>
                          )}
                          {s.status === "draft" && <Badge className="bg-amber-100 text-amber-800 border-transparent">DRAFT</Badge>}
                          <ArrowRight className="h-4 w-4 ml-auto text-primary" />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600" data-testid="today-empty">No sessions scheduled today.</p>
                  )}
                </div>
                {summary.my_training.notes_outstanding?.length > 0 && (
                  <div data-testid="notes-outstanding">
                    <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Session notes to complete ({summary.my_training.notes_outstanding.length})</p>
                    <div className="space-y-1.5">
                      {summary.my_training.notes_outstanding.map((s) => (
                        <Link key={s.id} to={`/sessions/${s.id}`} data-testid={`notes-session-${s.id}`}
                          className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 hover:bg-amber-100 transition-colors duration-200">
                          <StickyNote className="h-4 w-4" /> {s.child_name} — {s.date} <ArrowRight className="h-3.5 w-3.5 ml-auto" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                {summary.my_training.upcoming?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Upcoming training</p>
                    <div className="space-y-1.5">
                      {summary.my_training.upcoming.slice(0, 5).map((s) => (
                        <Link key={s.id} to={`/sessions/${s.id}`} data-testid={`upcoming-session-${s.id}`}
                          className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted transition-colors duration-200">
                          <span className="font-medium text-slate-800">{s.date}</span>
                          <span className="text-slate-600">{s.child_name}</span>
                          <span className="text-slate-500">{s.direction === "outbound" ? "H→S" : s.direction === "return" ? "S→H" : "Other"}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                <Link to="/schedule" data-testid="trainer-schedule-link"
                  className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover transition-colors duration-200">
                  <CalendarDays className="h-4 w-4" /> Open my schedule
                </Link>
              </CardContent>
            </Card>
          )}

          {user?.role === "travel_trainer" && summary.my_plans && (
            <Card data-testid="trainer-plans-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <RouteIcon className="h-5 w-5 text-primary" /> My route plans
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    ["draft", "Awaiting completion"], ["awaiting_review", "Awaiting review"],
                    ["approved", "Approved"], ["review_required", "Risk reviews due"],
                  ].map(([k, label]) => (
                    <div key={k} className="rounded-md border border-border p-3 text-center" data-testid={`my-plans-${k}`}>
                      <p className={`font-heading text-xl font-bold ${k === "review_required" && summary.my_plans[k] > 0 ? "text-red-700" : "text-slate-900"}`}>
                        {summary.my_plans[k] ?? 0}
                      </p>
                      <p className="text-xs text-slate-600">{label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {user?.role === "travel_trainer" && summary.my_assessments && (
            <Card data-testid="trainer-assessments-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ClipboardList className="h-5 w-5 text-primary" /> My assessments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    ["upcoming", "Upcoming"], ["draft", "Draft"], ["awaiting_information", "Awaiting info"],
                    ["submitted", "Submitted"], ["returned", "Returned"], ["completed", "Completed"],
                  ].map(([k, label]) => (
                    <div key={k} className="rounded-md border border-border p-3 text-center" data-testid={`my-assessments-${k}`}>
                      <p className="font-heading text-xl font-bold text-slate-900">{summary.my_assessments[k] ?? 0}</p>
                      <p className="text-xs text-slate-600">{label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {user?.role === "travel_trainer" && (
            <Card data-testid="trainer-children-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Baby className="h-5 w-5 text-primary" /> My assigned children
                </CardTitle>
              </CardHeader>
              <CardContent>
                {summary.my_children?.length ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="trainer-children-list">
                    {summary.my_children.map((c) => (
                      <Link key={c.id} to={`/children/${c.id}`} data-testid={`trainer-child-${c.case_id}`}
                        className="flex items-center gap-3 rounded-md border border-border p-3 hover:bg-muted transition-colors duration-200">
                        <StaffAvatar photoId={c.photo_document_id} name={c.name} />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 truncate">{c.name}{c.preferred_name ? ` (“${c.preferred_name}”)` : ""}</p>
                          <p className="text-xs text-slate-500 font-mono">{c.case_id} · {c.school_name || "No school linked"}</p>
                        </div>
                        <CaseStatusBadge status={c.case_status} />
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-600" data-testid="trainer-children-empty">
                    No children are currently allocated to you. New assignments will appear here and in your notifications.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {canSeeAttendance && exceptions && (
              <Card data-testid="attendance-exceptions-card" className="relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-500 via-amber-400 to-emerald-500" aria-hidden="true" />
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-heading text-base font-semibold text-slate-900">Staff attendance exceptions</h3>
                    <Link to="/live-staff" data-testid="exceptions-live-link" className="text-xs font-medium text-primary hover:underline">Live staff map</Link>
                  </div>
                  {exceptions.not_signed_in.length === 0 && exceptions.gps_silent.length === 0 && exceptions.long_shifts.length === 0 ? (
                    <p className="text-sm text-ukgreen flex items-center gap-2" data-testid="exceptions-all-clear">
                      <CheckCircle2 className="h-4 w-4" /> All trainers signed in, GPS sharing normally.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {exceptions.not_signed_in.length > 0 && (
                        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2" data-testid="exceptions-not-signed-in">
                          <p className="text-xs font-semibold text-red-800 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Not signed in today ({exceptions.not_signed_in.length})</p>
                          <p className="text-xs text-red-700 mt-0.5">{exceptions.not_signed_in.map((s) => s.name).join(", ")}</p>
                        </div>
                      )}
                      {exceptions.gps_silent.length > 0 && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2" data-testid="exceptions-gps-silent">
                          <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5" /> GPS silent mid-shift ({exceptions.gps_silent.length})</p>
                          <p className="text-xs text-amber-700 mt-0.5">{exceptions.gps_silent.map((s) => s.name).join(", ")}</p>
                        </div>
                      )}
                      {exceptions.long_shifts.length > 0 && (
                        <div className="rounded-md bg-sky-50 border border-sky-200 px-3 py-2" data-testid="exceptions-long-shifts">
                          <p className="text-xs font-semibold text-sky-800 flex items-center gap-1.5"><Timer className="h-3.5 w-3.5" /> Shift over 10 hours ({exceptions.long_shifts.length})</p>
                          <p className="text-xs text-sky-700 mt-0.5">{exceptions.long_shifts.map((s) => `${s.name} (${s.hours}h)`).join(", ")}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {isAdmin && (
              <Card data-testid="tasks-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    Tasks requiring attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {summary.tasks?.length ? (
                    <ul className="space-y-2">
                      {summary.tasks.map((t, i) => (
                        <li key={i} data-testid={`task-item-${i}`}>
                          <Link to={t.link || "/dashboard"} className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5 hover:bg-muted transition-colors duration-200">
                            <Badge className={t.severity === "high" ? "bg-red-50 text-red-700 border-transparent" : "bg-amber-100 text-amber-800 border-transparent"}>
                              {t.severity}
                            </Badge>
                            <span className="text-sm text-slate-700">{t.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-600" data-testid="tasks-empty">No outstanding tasks.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {hasPermission("children:read_all") && summary.children_by_council && (
              <Card data-testid="children-by-council-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="h-5 w-5 text-primary" /> Children by local authority
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {summary.children_by_council.length ? (
                    <ul className="divide-y divide-border">
                      {summary.children_by_council.map((r) => (
                        <li key={r.council_id} className="flex items-center justify-between py-2" data-testid={`council-count-${r.council_id}`}>
                          <span className="text-sm text-slate-700">{r.council_name}</span>
                          <span className="font-heading font-bold text-slate-900">{r.count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-600">No active cases yet.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {hasPermission("audit:read") && (
              <Card data-testid="recent-activity-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ClipboardList className="h-5 w-5 text-primary" />
                    Recent activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {summary.recent_activity?.length ? (
                    <ul className="divide-y divide-border">
                      {summary.recent_activity.map((a) => (
                        <li key={a.id} className="flex items-center gap-3 py-2" data-testid={`activity-${a.id}`}>
                          <Badge className={OUTCOME_STYLES[a.outcome] || ""}>{a.outcome}</Badge>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-800 truncate">{a.action}</p>
                            <p className="text-xs text-slate-500 truncate">{a.actor_email}</p>
                          </div>
                          <span className="text-xs text-slate-500 shrink-0">{fmtTime(a.created_at)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-600">No recent activity.</p>
                  )}
                  <Link to="/audit-logs" data-testid="view-full-audit-link" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                    View full audit log <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </CardContent>
              </Card>
            )}

            {isAdmin && (
              <Card data-testid="quick-actions-card">
                <CardHeader>
                  <CardTitle className="text-lg">Quick actions</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  <Link to="/children/new" data-testid="quick-action-add-child"
                    className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover transition-colors duration-200">
                    <Baby className="h-4 w-4" /> Add Child / Referral
                  </Link>
                  <Link to="/staff?new=1" data-testid="quick-action-add-staff"
                    className="inline-flex h-11 items-center gap-2 rounded-md border border-border bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-muted transition-colors duration-200">
                    <Plus className="h-4 w-4" /> Add Staff
                  </Link>
                  <Link to="/councils?new=1" data-testid="quick-action-add-council"
                    className="inline-flex h-11 items-center gap-2 rounded-md border border-border bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-muted transition-colors duration-200">
                    <Building2 className="h-4 w-4" /> Add Local Authority
                  </Link>
                  <Link to="/schools?new=1" data-testid="quick-action-add-school"
                    className="inline-flex h-11 items-center gap-2 rounded-md border border-border bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-muted transition-colors duration-200">
                    <School className="h-4 w-4" /> Add School
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
